import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import {
   EntitlementConfirmationType,
   EntitlementConfirmationTypeQuery,
   EntitlementType,
} from '@dreryk/drerykweb-medical-dictionaries-api';

import { UseCqrsQueryResult, useCqrsSearch } from '@dreryk/command-query-react-common';
import { createTranslatedText } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { patientEntitlementsTranslationKeys } from '../../translations';
import { PatientEntitlementCreateFormController } from '../create/PatientEntitlementCreateForm.types';

type EntitlementConfirmationTypesQueryParameters = {
   entitlementType: EntitlementType;
};

const useCqrsSearchEntitlementConfirmationTypes = (
   parameters: EntitlementConfirmationTypesQueryParameters
): UseCqrsQueryResult<EntitlementConfirmationType[]> => {
   return useCqrsSearch<EntitlementConfirmationType>(() => {
      const query = new EntitlementConfirmationTypeQuery();
      query.entitlementType = Slot.of(parameters.entitlementType);
      return query;
   });
};

const triggerLabelExtractor = (entitlementConfirmationType?: EntitlementConfirmationType): string =>
   entitlementConfirmationType?.name ?? '';

export type OnConfirmingDocumentTypeSelectItemsChanged = (items: EntitlementConfirmationType[]) => void;

type ConfirmingDocumentTypeSelectProps = Pick<PatientEntitlementCreateFormController, 'register'> & {
   readonly entitlementType: EntitlementType;
   readonly onConfirmingDocumentTypeSelectItemsChanged?: OnConfirmingDocumentTypeSelectItemsChanged;
};

export const ConfirmingDocumentTypeSelect = (props: ConfirmingDocumentTypeSelectProps): JSX.Element => {
   const controller = props.register('confirmingDocumentType', { required: true });
   const { data: entitlementConfirmationTypes } = useCqrsSearchEntitlementConfirmationTypes({
      entitlementType: props.entitlementType,
   });

   const items = useMemo(() => entitlementConfirmationTypes ?? [], [entitlementConfirmationTypes]);

   useUpdateEffect(() => {
      props.onConfirmingDocumentTypeSelectItemsChanged?.(items);
   }, [items]);

   return (
      <Select<EntitlementConfirmationType>
         triggerLabelExtractor={triggerLabelExtractor}
         controller={controller}
         label={patientEntitlementsTranslationKeys.confirmingDocumentName}
         placeholder={patientEntitlementsTranslationKeys.selectConfirmingDocumentName}
         testId={'confirmingDocumentType'}
      >
         <Select.List items={items} keyExtractor={'id'}>
            {(confirmationType: EntitlementConfirmationType) => (
               <Select.Item
                  item={confirmationType}
                  testId={confirmationType.id}
                  label={createTranslatedText(confirmationType.name)}
               />
            )}
         </Select.List>
      </Select>
   );
};
